<script setup lang="ts">
/**
 * People admitted to ONE assistant by name (ADR 0054).
 *
 * Entra is still the login. This is the second way into an assistant, next
 * to "your groups intersect its ACL groups": an admin adds the person here,
 * and that is the whole job — no Group IT ticket, no new Entra group.
 *
 * The rule for adding someone follows the assistant's access class
 * (ADR 0053): for a group-gated assistant a grant hands the person
 * everything its sources hold, so it must name who approved it. The panel
 * says that in words before the form, because it is the thing the admin
 * needs to know before typing.
 */
import { vWrite } from "../admin-read-only";
import { computed, onMounted, ref, watch } from "vue";
import {
  AdminError,
  type AssistantMember,
  type AssistantMembersResponse,
  type MemberScopeDef,
  grantAssistantMember,
  listAssistantMembers,
  revokeAssistantMember,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";

const props = defineProps<{ slug: string; devUser: string }>();

const toast = useToast();
const { confirm } = useConfirm();

const data = ref<AssistantMembersResponse | null>(null);
const loading = ref(false);
const saving = ref(false);

const email = ref("");
const approvedBy = ref("");
const note = ref("");
const expires = ref("");
/** scope name → the ids ticked (or typed) for the person being added. */
const picked = ref<Record<string, string[]>>({});
const typed = ref<Record<string, string>>({});
const moreOpen = ref(false);
const scopeFilter = ref<Record<string, string>>({});

/** The column exists only where narrowing does — for most assistants it
 *  would be a column of "everything". */
const showsNarrowing = computed(
  () =>
    (data.value?.scopes.length ?? 0) > 0 ||
    (data.value?.members ?? []).some((m) => Object.keys(m.scopes).length > 0),
);

/** A long option list, filtered — but never hiding what is already ticked,
 *  or a filter could make a choice invisible while it still applies. */
function visibleOptions(def: MemberScopeDef): MemberScopeDef["options"] {
  const q = (scopeFilter.value[def.name] ?? "").trim().toLowerCase();
  if (!q) return def.options;
  const chosen = new Set(picked.value[def.name] ?? []);
  return def.options.filter(
    (o) => chosen.has(o.key) || o.label.toLowerCase().includes(q),
  );
}

function fail(e: unknown, fallback: string): void {
  toast.error(e instanceof AdminError ? e.message : fallback);
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    data.value = await listAssistantMembers(props.slug, props.devUser);
  } catch (e) {
    fail(e, "Could not load members");
  } finally {
    loading.value = false;
  }
}

onMounted(load);
watch(() => props.slug, load);

const canAdd = computed(
  () =>
    email.value.trim().length > 0 &&
    (!data.value?.approval_required || approvedBy.value.trim().length > 0),
);

function toggle(scope: string, key: string): void {
  const cur = new Set(picked.value[scope] ?? []);
  if (cur.has(key)) cur.delete(key);
  else cur.add(key);
  picked.value = { ...picked.value, [scope]: Array.from(cur) };
}

function scopesForGrant(): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const def of data.value?.scopes ?? []) {
    const ids = new Set(picked.value[def.name] ?? []);
    for (const raw of (typed.value[def.name] ?? "").split(",")) {
      const t = raw.trim();
      if (t) ids.add(t);
    }
    if (ids.size) out[def.name] = Array.from(ids);
  }
  return out;
}

function scopeLabel(scope: string, key: string): string {
  const def = data.value?.scopes.find((s) => s.name === scope);
  return def?.options.find((o) => o.key === key)?.label ?? key;
}

async function add(): Promise<void> {
  if (!canAdd.value || saving.value) return;
  saving.value = true;
  try {
    await grantAssistantMember(
      props.slug,
      {
        member_email: email.value.trim(),
        approved_by: approvedBy.value.trim(),
        note: note.value.trim(),
        expires_at: expires.value || null,
        scopes: scopesForGrant(),
      },
      props.devUser,
    );
    toast.success(`${email.value.trim()} can now use this assistant`);
    email.value = "";
    note.value = "";
    expires.value = "";
    picked.value = {};
    typed.value = {};
    await load();
  } catch (e) {
    fail(e, "Could not add this person");
  } finally {
    saving.value = false;
  }
}

async function remove(m: AssistantMember): Promise<void> {
  const who = m.member_email || m.member_oid;
  const ok = await confirm({
    title: "Remove this person?",
    message: `${who} will no longer be able to open this assistant, unless one of their Entra groups also admits them.`,
    confirmLabel: "Remove",
  });
  if (!ok) return;
  try {
    await revokeAssistantMember(props.slug, m.member_oid, props.devUser);
    toast.success(`${who} removed`);
    await load();
  } catch (e) {
    fail(e, "Could not remove this person");
  }
}

function fmtDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}
</script>

<template>
  <div class="members">
    <p v-if="loading && !data" class="muted small">Loading…</p>
    <template v-else-if="data">
      <p class="hint">
        Add someone by e-mail and they can open this assistant — no Entra group
        needed<template v-if="data.acl_scope.length">
          (its groups, <code>{{ data.acl_scope.join(", ") }}</code
          >, still work)</template
        >.
      </p>
      <p :class="['rule', { 'rule--strict': data.approval_required }]">
        <strong>{{ data.access_class.label }}.</strong>
        {{ data.access_class.route.user_access }}
      </p>
      <p v-if="data.maturity === 'experimental'" class="small gap-text">
        This assistant is experimental, so only admins can open it — members
        included — until its release stage is changed.
      </p>

      <table v-if="data.members.length" class="table members__table">
        <thead>
          <tr>
            <th>Person</th>
            <th v-if="showsNarrowing">Narrowed to</th>
            <th>Approved by</th>
            <th>Added</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="m in data.members"
            :key="m.member_oid"
            :class="{ muted: m.expired }"
          >
            <td>
              {{ m.member_email || m.member_oid }}
              <div v-if="m.note" class="muted small">{{ m.note }}</div>
            </td>
            <td v-if="showsNarrowing">
              <template v-if="Object.keys(m.scopes).length">
                <div v-for="(keys, scope) in m.scopes" :key="scope">
                  <span v-for="k in keys" :key="k" class="pill">{{
                    scopeLabel(String(scope), k)
                  }}</span>
                </div>
              </template>
              <span v-else class="muted">everything</span>
            </td>
            <td>{{ m.approved_by || "—" }}</td>
            <td>
              {{ fmtDate(m.granted_at) }}
              <div class="muted small">
                by {{ m.granted_by_email || m.granted_by || "unknown" }}
              </div>
              <div v-if="m.expires_at" class="small">
                <span v-if="m.expired" class="gap-text">expired</span>
                <span v-else>until</span>
                {{ fmtDate(m.expires_at) }}
              </div>
            </td>
            <td>
              <button v-write class="btn btn--ghost btn--sm" @click="remove(m)">
                Remove
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="muted small">
        Nobody has been added by name. Everyone who can open this assistant gets
        in through an Entra group.
      </p>

      <!-- The everyday case is an address and a button. Everything else is
           optional, so it waits behind one disclosure — except "approved by"
           where the access class makes it mandatory, because a required field
           must never be a hidden one. -->
      <form v-write class="add" @submit.prevent="add">
        <input
          v-model="email"
          type="email"
          aria-label="E-mail of the person to add"
          placeholder="name@danoffice.com"
          autocomplete="off"
        />
        <input
          v-if="data.approval_required"
          v-model="approvedBy"
          type="text"
          maxlength="200"
          aria-label="Approved by (required)"
          placeholder="Approved by — the data owner"
        />
        <button
          class="btn btn--primary"
          type="submit"
          :disabled="!canAdd || saving"
        >
          {{ saving ? "Adding…" : "Add person" }}
        </button>
        <button
          type="button"
          class="link"
          :aria-expanded="moreOpen"
          @click="moreOpen = !moreOpen"
        >
          {{ moreOpen ? "fewer options" : "more options" }}
        </button>
      </form>

      <div v-if="moreOpen" class="members__form">
        <label v-if="!data.approval_required">
          Approved by (optional)
          <input v-model="approvedBy" type="text" maxlength="200" />
        </label>
        <label>
          Until (optional)
          <input v-model="expires" type="date" />
        </label>
        <label class="members__wide">
          Note (optional)
          <input
            v-model="note"
            type="text"
            maxlength="500"
            placeholder="Why, or the ticket it belongs to"
          />
        </label>
        <fieldset
          v-for="def in data.scopes"
          :key="def.name"
          class="members__wide scope"
        >
          <legend>
            Narrow to {{ def.label }} (optional)<template
              v-if="(picked[def.name] ?? []).length"
            >
              · {{ (picked[def.name] ?? []).length }} chosen</template
            >
          </legend>
          <p class="hint">
            Leave empty for no narrowing. Once the source is partitioned this
            way, a person with nothing ticked sees none of it.
          </p>
          <template v-if="def.options.length">
            <input
              v-if="def.options.length > 12"
              v-model="scopeFilter[def.name]"
              type="search"
              :aria-label="`Filter ${def.label}`"
              :placeholder="`Filter ${def.options.length} ${def.label.toLowerCase()}…`"
            />
            <div class="scope__options">
              <label
                v-for="o in visibleOptions(def)"
                :key="o.key"
                class="checkbox"
              >
                <input
                  type="checkbox"
                  :checked="(picked[def.name] ?? []).includes(o.key)"
                  @change="toggle(def.name, o.key)"
                />
                {{ o.label }}
              </label>
            </div>
          </template>
          <input
            v-else
            v-model="typed[def.name]"
            type="text"
            placeholder="Ids, comma-separated"
          />
        </fieldset>
      </div>
    </template>
  </div>
</template>

<style scoped>
.add {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.add input,
.members__form label > input,
.scope input[type="search"],
.scope input[type="text"] {
  flex: 1 1 14rem;
  min-width: 0;
  font: inherit;
  font-size: 0.92rem;
  color: var(--color-ink);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 0.45rem 0.6rem;
}
.add input:focus,
.members__form label > input:focus,
.scope input:focus {
  outline: none;
  border-color: var(--color-accent);
}
.members__form label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.82rem;
  color: var(--color-muted);
}
.scope .hint {
  margin: 0 0 0.4rem;
  font-size: 0.82rem;
  color: var(--color-muted);
}
.scope legend {
  font-size: 0.85rem;
  font-weight: 600;
}
.members > .muted.small {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.members > .hint {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.scope input[type="search"] {
  width: 100%;
  margin-bottom: 0.4rem;
}
button.link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.85rem;
  color: var(--color-accent);
  cursor: pointer;
}
.members {
  display: flex;
  flex-direction: column;
  gap: var(--space-2, 0.5rem);
}
.rule {
  margin: 0;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid var(--border, #ccc);
  font-size: 0.85rem;
}
.rule--strict {
  border-left-color: var(--warn, #b7791f);
}
.members__table td {
  vertical-align: top;
}
.members__form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 0.5rem;
  align-items: end;
}
.members__wide {
  grid-column: 1 / -1;
}
.scope {
  border: 1px solid var(--border, #ccc);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
}
.scope__options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  gap: 0.15rem 0.75rem;
  max-height: 12rem;
  overflow: auto;
}
.req {
  font-size: 0.75rem;
  color: var(--warn, #b7791f);
  margin-left: 0.35rem;
}
.pill {
  display: inline-block;
  margin: 0 0.25rem 0.15rem 0;
}
</style>
